import {
  EDesignerEntityKind,
  ESdChangeReason,
  IActionDefinitionDto,
  IDatabaseTableModel,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import * as React from "react";
import { DropResult } from "react-beautiful-dnd";
import { connect } from "react-redux";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  RECENTLY_MODIFIED_TAB,
  RECENTLY_MODIFIED_TAB_TEST_ID,
  RECENTLY_MODIFIED_WINDOW_DAYS,
  resolveActiveTab
} from "../../../shared/utility/recently-modified";
import { buildLastModifiedColumn } from "../../../shared/utility/recently-modified-columns";
import {
  createFeedRequestGuard,
  haveItemIdsChanged,
  loadModificationFeed,
  LOADING_FEED,
  type IFeedRequestGuard,
  type IModificationFeed
} from "../../../shared/utility/recently-modified-feed";
import { SaveStatus } from "../../../shared/utility/save-status";
import { showDomainAccessRequiredModal } from "../../../store/domain-access-modal/actions";
import { setNextCallbackFunction } from "../../../store/unsaved-changes-modal/actions";
import { DeleteModal } from "../../common/delete-modal";
import FolderModal, { type IFolderFormValues } from "../../common/folder-modal";
import KuikaButton, { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaListPage, {
  KuikaCreateCard,
  KuikaCreateCardRow,
  KuikaListPageHeader,
  KuikaListPageToolbar
} from "../../ui-kit/kuika-list-page/kuika-list-page";
import KuikaListTable, {
  KuikaListTableColumn,
  KuikaListTableNameCell,
  KuikaListTableRowAction,
  KuikaListTableStatusCell,
  KuikaListTableUserCell
} from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaLoader from "../../ui-kit/kuika-loader/kuika-loader";
import { KuikaNameModal } from "../../ui-kit/kuika-name-modal/kuika-name-modal";
import KuikaNumber from "../../ui-kit/kuika-number/kuika-number";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabContent from "../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { ActionUsagesModal } from "../left-panel/datasource-panel/action-usages-modal";
import { ConnectorPickerModal } from "./connector-picker-modal";
import { ConnectorPickerPopover } from "./connector-picker-popover";
import { DatabaseDiagram } from "./database-diagram";
import {
  getDatasourceRenameNames,
  getDatasourceRenameProblem,
  getTableRenameNames,
  getTableRenameProblem,
  renameDatasourceFromRow,
  renameTableFromRow
} from "./datasource-rename";
import { subscribeDatasourceReveal, takeDatasourceReveal, type IDatasourceRevealRequest } from "./datasource-reveal";
import {
  canEditAction,
  canManageActions,
  canManageDatasources,
  canManageTables,
  deleteActionFromRow,
  deleteDatasourceFromRow,
  deleteRestEndpointFromRow,
  deleteTableFromRow,
  findAgentUsagesOfAction,
  findRestEndpointIndex,
  getNewActionKindLabel,
  getRestEndpointName,
  isTableEditable,
  refreshThirdPartyDatasource
} from "./datasource-row-actions";
import {
  DatasourceListRowMenuKey,
  getDatasourceListRowMenu,
  getDatasourceRowAccess,
  IDatasourceListRowMenuContext
} from "./datasource-row-menus";
import {
  applyDatasourceRowMove,
  DatasourceFolderDialog,
  datasourceFolderNameExists,
  deleteDatasourceFolder,
  IDatasourceTreeActionDeps,
  startNewAction,
  startNewActionInFolder,
  startNewRestEndpoint,
  startNewSelectAction,
  startNewTable,
  submitDatasourceFolderDialog
} from "./datasource-tree-actions";
import "./datasources-landing.scss";
import {
  buildDatasourceRecentRows,
  buildDatasourceRows,
  DATASOURCE_CARD_TINTS,
  DatasourceCreateCard,
  DatasourceCreateCategory,
  DatasourceListFolderRow,
  DatasourceListRow,
  DatasourceRowKind,
  DatasourceRowMove,
  datasourceRecentsFromFeed,
  DatasourcesCentreView,
  findDatasourceRowPath,
  flattenDatasourceRows,
  getDatasourceDisplayName,
  getDatasourceIconName,
  getDatasourceListChildren,
  getDatasourceRowDragGroupId,
  getDatasourceTypeLabel,
  hasDatasourceFolderFor,
  IDatasourceDropTarget,
  IDatasourceListActionRow,
  IDatasourceListDatasourceRow,
  IDatasourceListSessionVariablesRow,
  IDatasourceListTableRow,
  IDatasourceRecent,
  IDatasourceRecentRow,
  IDatasourceRowDragContext,
  indexDatasourceDropTargets,
  isDatasourceRowDraggable,
  isListableDatasource,
  isManagedDatasource,
  IDatasourceSchemaLike,
  resolveDatasourceRowMove
} from "./datasources-landing-model";

/**
 * The Datasources landing page: everything the project connects to, as one tree — a datasource
 * per row, beneath it the same Actions / Tables groups, schemas and folders the Sources tree
 * shows — above the cards that create more.
 *
 * It replaces the three-column "Add a new Datasource" screen that used to be the tool's only
 * centre besides the ER diagram, which is now a tab rather than the default. The LEFT TREE
 * stays and stays visible: unlike the panels the other landings retired, it is also Document
 * Parameters' left panel and the query builder's, and it is where every editor is reached from
 * while one is open.
 *
 * Columns are what the model actually carries. Only `IActionDefinitionDto` has `createdAt` and
 * `createdUser`; datasources and tables carry neither, so the list's Created column is filled on
 * action rows alone. When a datasource or an action was last written, and by whom, comes from the
 * shared entity-modification feed instead — which is what the "Recently modified" tab the page
 * opens on reads. A table is never on that tab: its schema is read live from the customer's
 * database, and nothing on the platform records when it changed.
 */

// Only the two views that hold their data in hand are tabs, so a switch is always instant — the
// feed behind the recent tab is fetched once, when the page mounts, not on the way into it. The
// diagram fetches the schema when it opens — that was the one slow switch — so it is now the
// managed database row's own action, and this page offers a way back instead of a tab.
const LIST_TAB: string = "Data sources";

/** The tabs in their on-screen order, so a switch slides towards the one chosen. */
const TAB_ORDER: string[] = [RECENTLY_MODIFIED_TAB, LIST_TAB];

/** The Kind column sorts on the label it draws, not on the internal kind. */
const RECENT_KIND_LABELS: Record<DatasourceRowKind, string> = {
  datasource: "Data source",
  table: "Table",
  action: "Action"
};

const KIND_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "datasource", label: "Data sources" },
  { value: "table", label: "Tables" },
  { value: "action", label: "Actions" }
];

/**
 * Every word "New … Action" can carry (`getNewActionKindLabel`). A menu entry's label is fixed, so
 * each kind is its own entry, shown only on the Actions group whose datasource makes that kind.
 */
const NEW_ACTION_KIND_LABELS: string[] = ["SQL", "Blockly", "TypeScript", "SAP"];

/** The rows whose own editor a click opens. */
type DatasourceOpenableRow =
  | IDatasourceListDatasourceRow
  | IDatasourceListTableRow
  | IDatasourceListActionRow
  | IDatasourceListSessionVariablesRow;

/**
 * A row's menu (`getDatasourceListRowMenu`), resolved once per render for every row drawn: the
 * row actions, the row click, the inert-row class and whether there is an actions column at all
 * all read the same answer.
 */
type DatasourceRowMenuLookup = (row: DatasourceListRow) => DatasourceListRowMenuKey[];

interface IDatasourceRenameTargetDatasource {
  kind: "datasource";
  datasource: IDatasourceDto;
}

interface IDatasourceRenameTargetTable {
  kind: "table";
  datasource: IDatasourceDto;
  table: IDatabaseTableModel;
}

/** What the rename dialog is open for. Folders rename through the folder dialog instead. */
type DatasourceRenameTarget = IDatasourceRenameTargetDatasource | IDatasourceRenameTargetTable;

/** A row that neither opens nor toggles keeps the kit's hover but drops its pointer. */
const INERT_ROW_CLASS: string = "kuika-datasources__row--inert";

interface IDatasourcesLandingState {
  /** The action whose Where Used report is open, or undefined while it is closed. */
  actionToInspectUsages: IActionDefinitionDto | undefined;
  searchText: string;
  kindFilter: DatasourceRowKind | "all";
  /**
   * The datasources whose children are showing. A datasource can hold hundreds of tables, so
   * the tree opens CLOSED and remembers what was opened — the opposite of the AI Functions
   * folder tree, where a handful of children makes open-by-default the kinder start.
   */
  expandedIds: string[];
  /**
   * The groups, schemas and folders the user CLOSED. Inside an open datasource they start open,
   * as AI Functions' folders do, so one created later is open without anyone seeding it.
   */
  collapsedIds: string[];
  /** What the folder dialog is open for, or null while it is closed. */
  folderDialog: DatasourceFolderDialog | null;
  /**
   * Bumped after a folder write. The folder provider edits `project.actionFolders` /
   * `databaseTablesFolders` in place and re-renders only once the project PATCH returns, so this
   * is what repaints the tree with the move now.
   */
  folderNonce: number;
  /** The tab the user chose, or null while the page is still deciding for itself. */
  pickedTab: string | null;
  feed: IModificationFeed;
  /**
   * Set only by `consumeCreateIntent` (`sd.datasourceCreateIntent`) — an opener outside this page
   * asking through the provider: the left tree's "+" (always "all") and a switcher chip's
   * "create new" (e.g. "function"). Still opens `ConnectorPickerModal`; a card click never
   * touches this.
   */
  pickerCategory?: DatasourceCreateCategory;
  /** Which of the three create cards' own popover is open, at most one at a time. */
  openCardCategory?: DatasourceCreateCard;
  /** The row whose typed-confirmation delete modal is open. */
  rowToDelete?: DatasourceListRow;
  /** The datasource or table the rename dialog is open for, or null while it is closed. */
  renameTarget: DatasourceRenameTarget | null;
  /** Where the rename's write stands; see `renameStatus_`. */
  renameStatus: KuikaButtonStatus;
  /**
   * The name of the row an editor asked to be lit up as it closed. Held as the NAME because the
   * id is resolved against whatever rows are loaded at the time — the list is often still being
   * refreshed when this page mounts. Cleared once the table reports the row drawn.
   */
  revealRowName: string | null;
}

/** First letters of an action author, who reaches this screen as a name and nothing else. */
function getCreatedUserInitials(user: { firstName?: string; lastName?: string } | undefined): string | undefined {
  const initials: string = `${user?.firstName?.charAt(0) ?? ""}${user?.lastName?.charAt(0) ?? ""}`.toUpperCase();
  return initials.length > 0 ? initials : undefined;
}

/**
 * The id the modification feed knows a row by. Only a datasource and an action are dated: a table
 * has no timestamp anywhere on the platform, and groups, schemas and folders are not entities.
 */
function getModifiedEntityId(row: DatasourceListRow): string | undefined {
  if (row.kind === "datasource") return row.datasource.id;
  if (row.kind === "action") return row.action.id;
  return undefined;
}

/**
 * The name a datasource answers to in test ids. The managed database's own name differs per
 * project, so it is "ManagedDB" — the name the Sources tree already gives it
 * (`getDatasourceTestIdName`), and the one row whose menu opens the ER diagram.
 */
function getDatasourceTestIdName(datasource: IDatasourceDto): string {
  if (isManagedDatasource(datasource)) return "ManagedDB";
  return (getDatasourceDisplayName(datasource) ?? "").replace(/\s/g, "");
}

/**
 * The name a row's menu id is built from. Every datasource has an "Actions" and a "Tables" group,
 * most have a "dbo" schema, and only the managed database has Session Variables, so those rows are
 * prefixed with their datasource's — `ManagedDBActionsRowMenu`, `WarehouseSchema-dboRowMenu`. A
 * folder is prefixed with its kind, since an action folder and a table folder can share a name.
 */
function getRowTestIdName(row: DatasourceListRow): string {
  if (row.kind === "datasource") return isManagedDatasource(row.datasource) ? "ManagedDB" : row.name;
  if (row.kind === "group" || row.kind === "sessionVariables") {
    return `${getDatasourceTestIdName(row.datasource)}${row.name.replace(/\s/g, "")}`;
  }
  if (row.kind === "schema") return `${getDatasourceTestIdName(row.datasource)}Schema-${row.name}`;
  if (row.kind === "folder") return `${row.folderKind === "action" ? "Action" : "Table"}Folder-${row.name}`;
  return row.name;
}

/** Whether the row holds other rows — a datasource, a group, a schema or a folder. */
function isContainerRow(row: DatasourceListRow): boolean {
  return getDatasourceListChildren(row) !== undefined;
}

function isOpenableRow(row: DatasourceListRow): row is DatasourceOpenableRow {
  return row.kind === "datasource" || row.kind === "table" || row.kind === "action" || row.kind === "sessionVariables";
}

/** The name the rename dialog starts from and compares against. */
function getRenameTargetName(target: DatasourceRenameTarget): string {
  return target.kind === "table" ? target.table.name : target.datasource.name;
}

/** The Swagger-less REST endpoint an action row stands for, if it stands for one. */
function getRowRestEndpointIndex(row: DatasourceListRow): number | undefined {
  if (row.kind !== "action") return undefined;
  return findRestEndpointIndex(row.datasource, row.action);
}

export interface IDatasourcesLandingProps {
  /** Redux `setNextCallbackFunction` — see `runGuarded`. */
  setNextCallbackFunction: (cb: () => void) => void;
  /** Redux: the plan/quota modal a group's "New … Action" raises when the domain is out of room. */
  showDomainAccessRequiredModal: (accessName: string | string[]) => void;
}

class DatasourcesLanding extends PureSdComponent<IDatasourcesLandingProps & IWithSDProps, IDatasourcesLandingState> {
  private isMounted_: boolean = false;
  /** Guards `loadFeed` against an out-of-order reply when the item set changes twice in a row. */
  private feedGuard_: IFeedRequestGuard = createFeedRequestGuard();

  constructor(props: IDatasourcesLandingProps & IWithSDProps) {
    super(props);
    // An editor that has just saved hands over the row it landed on; this page is mounted by that
    // editor's close, so the hand-over is taken here rather than watched for.
    const reveal: IDatasourceRevealRequest | null = takeDatasourceReveal();
    this.state = {
      actionToInspectUsages: undefined,
      searchText: "",
      kindFilter: "all",
      // The tree opens closed, so a new action under a datasource has to have its parent opened
      // for it to be in the list the reveal looks through at all.
      expandedIds: reveal?.parentId ? [reveal.parentId] : [],
      collapsedIds: [],
      folderDialog: null,
      folderNonce: 0,
      pickedTab: null,
      feed: LOADING_FEED,
      pickerCategory: undefined,
      openCardCategory: undefined,
      rowToDelete: undefined,
      renameTarget: null,
      renameStatus: "idle",
      revealRowName: reveal?.name ?? null
    };
  }

  /** Holds the rename's ✓ on the confirm before the dialog closes. */
  private renameStatus_: SaveStatus = new SaveStatus((renameStatus: KuikaButtonStatus): void => {
    this.setState({ renameStatus });
  });

  /** Ends the subscription that lets a saver over this page (the panel's SQL dialogs) reveal a row. */
  private unsubscribeReveal: (() => void) | undefined = undefined;

  componentDidMount(): void {
    this.isMounted_ = true;
    this.consumeCreateIntent();
    this.unsubscribeReveal = subscribeDatasourceReveal(this.handleRevealRequest);
    void this.loadFeed();
    this.openPathToRevealRow();
  }

  componentWillUnmount(): void {
    this.isMounted_ = false;
    this.unsubscribeReveal?.();
    this.renameStatus_.dispose();
  }

  /** A save landed while this page is on screen: light its row, opening its datasource first. */
  handleRevealRequest = (request: IDatasourceRevealRequest): void => {
    this.setState((prev: IDatasourcesLandingState) => ({
      revealRowName: request.name,
      expandedIds:
        request.parentId && !prev.expandedIds.includes(request.parentId)
          ? [...prev.expandedIds, request.parentId]
          : prev.expandedIds
    }));
  };

  componentDidUpdate(prevProps: Readonly<IDatasourcesLandingProps & IWithSDProps>): void {
    this.consumeCreateIntent();
    this.openPathToRevealRow();

    // This page is usually remounted by every editor's close, which is what the comment on
    // `loadFeed` below relies on — but a table's row action (Delete, Rename) and a datasource's
    // own Delete both mutate `datasources`/`actionDefinitions` while this page stays mounted, so
    // the id set is still watched here rather than only trusted to the remount.
    const previousDatasources: IDatasourceDto[] = prevProps.sd?.datasources ?? [];
    const previousActions: IActionDefinitionDto[] = prevProps.sd?.actionDefinitions ?? [];
    const datasourcesChanged: boolean = haveItemIdsChanged(
      previousDatasources,
      this.props.sd?.datasources ?? [],
      (row: IDatasourceDto) => row.id
    );
    const actionsChanged: boolean = haveItemIdsChanged(
      previousActions,
      this.props.sd?.actionDefinitions ?? [],
      (row: IActionDefinitionDto) => row.id
    );
    if (datasourcesChanged || actionsChanged) {
      void this.loadFeed();
    }
  }

  /**
   * Fetched on every mount, and this page is remounted by every editor's close — so what was just
   * saved in one is on the recent tab when the page comes back. Tables are not asked for: nothing
   * on the platform dates them.
   */
  loadFeed = async (): Promise<void> => {
    const token: number = this.feedGuard_.start();
    const projectId: string | undefined = this.props.sd?.project?.id;
    const feed: IModificationFeed = await loadModificationFeed(
      [EDesignerEntityKind.Datasource, EDesignerEntityKind.Action],
      projectId
    );
    if (!this.isMounted_ || !this.feedGuard_.isCurrent(token)) return;
    this.setState({ feed });
  };

  /**
   * Everything the feed dates, over EVERY datasource and action — never the searched ones: a query
   * that matches nothing recent must not move the user to another tab mid-keystroke.
   */
  getFeedRecents = (): IDatasourceRecent[] =>
    datasourceRecentsFromFeed(
      this.props.sd?.datasources ?? [],
      this.props.sd?.actionDefinitions ?? [],
      this.state.feed.index,
      Date.now()
    );

  /**
   * Derived rather than stored, so it cannot go stale against the feed: the page opens on the
   * recent tab, drops to the full list when nothing is recent or the feed failed, and otherwise
   * shows whatever the user picked.
   */
  getActiveTab = (feedRecents: IDatasourceRecent[]): string =>
    resolveActiveTab({
      pickedTab: this.state.pickedTab,
      feedStatus: this.state.feed.status,
      hasRecent: feedRecents.length > 0,
      // The feed can answer before the provider's datasource list lands; deciding then would read
      // "nothing is recent" off an empty list and jump tabs when the list arrives. A loaded list is
      // never empty — the server inserts the default managed database when a workspace has none.
      itemsReady: (this.props.sd?.datasources?.length ?? 0) > 0,
      recentTab: RECENTLY_MODIFIED_TAB,
      fallbackTab: LIST_TAB
    });

  /** The left tree's "+" asks for the connector sheet through the provider; this opens it once. */
  consumeCreateIntent = (): void => {
    const intent: DatasourceCreateCategory | undefined = this.props.sd?.datasourceCreateIntent;
    if (!intent) return;
    this.props.sd.requestDatasourceCreate(undefined);
    this.setState({ pickerCategory: intent });
  };

  // --- rows -----------------------------------------------------------------

  getRows = (): IDatasourceListDatasourceRow[] =>
    buildDatasourceRows({
      datasources: this.props.sd?.datasources ?? [],
      schemas: (this.props.sd?.schemas ?? new Map()) as Map<string, IDatasourceSchemaLike>,
      actions: this.props.sd?.actionDefinitions ?? [],
      actionFolders: this.props.sd?.project?.actionFolders ?? [],
      tableFolders: this.props.sd?.project?.databaseTablesFolders ?? [],
      searchText: this.state.searchText,
      kindFilter: this.state.kindFilter
    });

  /** A search or a Kind filter narrows the tree to what matched. */
  isFiltering = (): boolean => this.state.searchText.trim().length > 0 || this.state.kindFilter !== "all";

  isRowExpanded = (row: DatasourceListRow): boolean => {
    if (!isContainerRow(row)) return false;
    // A search or a filter is a request to see what matched, so the path to every match is open.
    if (this.isFiltering()) return true;
    if (row.kind === "datasource") return this.state.expandedIds.includes(row.id);
    return !this.state.collapsedIds.includes(row.id);
  };

  /** Datasources remember what was OPENED (they start closed); everything below, what was CLOSED. */
  toggleRow = (row: DatasourceListRow): void => {
    this.setState((prev: IDatasourcesLandingState): IDatasourcesLandingState => {
      if (row.kind === "datasource") {
        const isOpen: boolean = prev.expandedIds.includes(row.id);
        return {
          ...prev,
          expandedIds: isOpen
            ? prev.expandedIds.filter((id: string): boolean => id !== row.id)
            : [...prev.expandedIds, row.id]
        };
      }
      const isClosed: boolean = prev.collapsedIds.includes(row.id);
      return {
        ...prev,
        collapsedIds: isClosed
          ? prev.collapsedIds.filter((id: string): boolean => id !== row.id)
          : [...prev.collapsedIds, row.id]
      };
    });
  };

  // --- opening --------------------------------------------------------------

  /**
   * Opens the row's own editor. A row that holds others toggles instead — its children are the
   * point — and a row whose menu offers no Edit does nothing here either, rather than opening a
   * surface the menu says does not exist. Such a row is drawn without a pointer (`isInertRow`).
   */
  handleRowClick = (row: DatasourceListRow, menuOf: DatasourceRowMenuLookup): void => {
    if (isContainerRow(row)) {
      this.toggleRow(row);
      return;
    }
    if (!isOpenableRow(row) || !menuOf(row).includes("edit")) return;
    this.openRow(row);
  };

  /** A row that neither toggles nor opens. `KuikaTable` points at every row of a clickable table. */
  isInertRow = (row: DatasourceListRow, menuOf: DatasourceRowMenuLookup): boolean =>
    !isContainerRow(row) && !menuOf(row).includes("edit");

  /**
   * Opening an editor while another one is dirty goes through the unsaved-changes modal, exactly
   * as the tree's own Edit items do (`handleEditDatasource`): the open editor asks first, and the
   * parked callback runs only if the user gives it up. Without this the landing would discard an
   * unsaved query or column set silently.
   */
  runGuarded = (open: () => void): void => {
    if (
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName
    ) {
      this.props.setNextCallbackFunction(open);
      return;
    }
    open();
  };

  /**
   * The editor the tree's Edit opens for the same row: a table outside the project's schema opens
   * read-only, and a Swagger-less REST action opens its endpoint in the datasource's panel.
   */
  openRow = (row: DatasourceOpenableRow): void => {
    this.runGuarded((): void => {
      if (row.kind === "datasource") {
        this.props.sd.setActiveEditingDatasource(row.datasource);
        return;
      }
      if (row.kind === "table") {
        const isReadOnly: boolean = !isTableEditable(this.props.sd, row.table);
        this.props.sd.setEditingTablesDatasource(row.datasource, row.table, false, undefined, isReadOnly);
        return;
      }
      if (row.kind === "sessionVariables") {
        // The Sources tree's Session Variables "Edit".
        this.props.sd.setEditingSessionVariables(true);
        return;
      }
      const endpointIndex: number | undefined = findRestEndpointIndex(row.datasource, row.action);
      if (endpointIndex !== undefined) {
        this.props.sd.setActiveEditingDatasource(row.datasource, endpointIndex);
        return;
      }
      this.props.sd.setEditingActionsDatasource(row.datasource, row.action, false, false);
    });
  };

  /** What the group rows' create entries need from this page. */
  getTreeActionDeps = (): IDatasourceTreeActionDeps => ({
    sd: this.props.sd,
    showDomainAccessRequiredModal: (accessName: string): void => this.props.showDomainAccessRequiredModal(accessName),
    runGuarded: this.runGuarded
  });

  // --- row actions ----------------------------------------------------------
  // The tree's three-dot menus, on the rows that now show the same entities. WHICH entries a row
  // offers is `getDatasourceListRowMenu`'s answer — the one the tree draws from too; what each
  // entry does lives here.

  /** What `getDatasourceListRowMenu` needs from the project, read once for a whole render. */
  getRowMenuContext = (): IDatasourceListRowMenuContext => ({
    access: getDatasourceRowAccess(this.props.sd),
    isTableEditable: (table: IDatabaseTableModel): boolean => isTableEditable(this.props.sd, table),
    isTemplateAction: (action: IActionDefinitionDto): boolean => !canEditAction(this.props.sd, action)
  });

  /** Every drawn row's menu, resolved once; a row it has not seen is resolved on demand. */
  buildRowMenuLookup = (rows: DatasourceListRow[]): DatasourceRowMenuLookup => {
    const context: IDatasourceListRowMenuContext = this.getRowMenuContext();
    const menus: Map<string, DatasourceListRowMenuKey[]> = new Map<string, DatasourceListRowMenuKey[]>();
    rows.forEach((row: DatasourceListRow): void => {
      menus.set(row.id, getDatasourceListRowMenu(row, context));
    });
    return (row: DatasourceListRow): DatasourceListRowMenuKey[] =>
      menus.get(row.id) ?? getDatasourceListRowMenu(row, context);
  };

  /**
   * What "New Folder" creates on this row: a top-level action folder on an Actions group, a
   * sub-folder on a top-level action folder (one level deep, as the provider writes them). Never a
   * table folder — the project does not save them. The menu decides whether the row offers it.
   */
  getNewFolderDialog = (row: DatasourceListRow): DatasourceFolderDialog | null => {
    if (row.kind === "group" && row.groupKind === "actions") {
      return { mode: "create", folderKind: "action", datasourceId: row.datasource.id };
    }
    if (row.kind === "folder" && row.folderKind === "action" && !row.folder.parentFolderId) {
      return { mode: "create", folderKind: "action", datasourceId: row.datasource.id, parentFolder: row.folder };
    }
    return null;
  };

  /**
   * The Sources tree's "New … Action" entries, one per kind, each on the rows whose datasource
   * makes that kind: an Actions group, and an action folder (which files the new action in it).
   */
  getNewActionRowActions = (menuOf: DatasourceRowMenuLookup): KuikaListTableRowAction<DatasourceListRow>[] =>
    NEW_ACTION_KIND_LABELS.map((kindLabel: string): KuikaListTableRowAction<DatasourceListRow> => ({
      key: `new-action-${kindLabel.toLowerCase()}`,
      label: `New ${kindLabel} Action`,
      icon: <KuikaSvg width={16} height={16} name="plus" />,
      hidden: (row: DatasourceListRow): boolean =>
        !menuOf(row).includes("new-action") || getNewActionKindLabel(row.datasource) !== kindLabel,
      onSelect: (row: DatasourceListRow): void => {
        if (row.kind === "folder" && row.folderKind === "action") {
          startNewActionInFolder(this.getTreeActionDeps(), row.datasource, row.folder.id);
          return;
        }
        if (row.kind === "group") startNewAction(this.getTreeActionDeps(), row.datasource);
      }
    }));

  /** Opens the dialog that renames the row: the folder dialog for a folder, the name sheet otherwise. */
  openRenameDialog = (row: DatasourceListRow): void => {
    if (row.kind === "folder") {
      const dialog: DatasourceFolderDialog =
        row.folderKind === "action"
          ? { mode: "rename", folderKind: "action", folder: row.folder }
          : { mode: "rename", folderKind: "table", folder: row.folder };
      this.setState({ folderDialog: dialog });
      return;
    }
    if (row.kind === "datasource") {
      this.setState({ renameTarget: { kind: "datasource", datasource: row.datasource } });
      return;
    }
    if (row.kind === "table") {
      this.setState({ renameTarget: { kind: "table", datasource: row.datasource, table: row.table } });
    }
  };

  /**
   * Every entry a row menu can hold, in one order; each row shows the ones its menu names. Every
   * entry calls what the tree's own entry calls.
   */
  getRowActions = (menuOf: DatasourceRowMenuLookup): KuikaListTableRowAction<DatasourceListRow>[] => {
    // An entry is hidden on every row whose menu does not name it.
    const hiddenUnlessOffered: (key: DatasourceListRowMenuKey) => (row: DatasourceListRow) => boolean =
      (key: DatasourceListRowMenuKey) =>
      (row: DatasourceListRow): boolean =>
        !menuOf(row).includes(key);

    return [
      {
        key: "edit",
        label: "Edit",
        icon: <KuikaSvg width={16} height={16} name="edit" />,
        hidden: hiddenUnlessOffered("edit"),
        onSelect: (row: DatasourceListRow): void => {
          if (isOpenableRow(row)) this.openRow(row);
        }
      },
      {
        key: "where-used",
        label: "Where Used",
        icon: <KuikaSvg width={16} height={16} name="list-search" />,
        hidden: hiddenUnlessOffered("where-used"),
        onSelect: (row: DatasourceListRow): void => {
          if (row.kind === "action") this.setState({ actionToInspectUsages: row.action });
        }
      },
      {
        key: "browse",
        label: "Browse Data",
        icon: <KuikaSvg width={16} height={16} name="table" />,
        hidden: hiddenUnlessOffered("browse"),
        onSelect: (row: DatasourceListRow): void => {
          if (row.kind !== "table") return;
          this.props.sd.setBrowsingRowsTable(row.datasource, row.table);
        }
      },
      {
        key: "diagram",
        label: "Open ER diagram",
        icon: <KuikaSvg width={16} height={16} name="route-square-2" />,
        // The diagram is drawn from the managed database's own schema (`databaseDiagramData`),
        // so it belongs to that row rather than to a tab of its own.
        hidden: hiddenUnlessOffered("diagram"),
        onSelect: (): void => this.props.sd.setDatasourcesCentreView("diagram")
      },
      {
        key: "create-table",
        label: "Create table",
        icon: <KuikaSvg width={16} height={16} name="plus" />,
        hidden: hiddenUnlessOffered("create-table"),
        onSelect: (row: DatasourceListRow): void => {
          if (row.kind !== "datasource") return;
          startNewTable(this.getTreeActionDeps(), row.datasource);
        }
      },
      {
        key: "refresh",
        label: "Refresh",
        icon: <KuikaSvg width={16} height={16} name="refresh" />,
        hidden: hiddenUnlessOffered("refresh"),
        onSelect: (row: DatasourceListRow): void => {
          if (row.kind !== "datasource") return;
          void refreshThirdPartyDatasource(this.props.sd, row.datasource);
        }
      },
      ...this.getNewActionRowActions(menuOf),
      {
        key: "new-select-action",
        label: "New Select Action",
        icon: <KuikaSvg width={16} height={16} name="plus" />,
        hidden: hiddenUnlessOffered("new-select-action"),
        onSelect: (row: DatasourceListRow): void => {
          if (row.kind === "group") startNewSelectAction(this.getTreeActionDeps(), row.datasource);
        }
      },
      {
        key: "new-rest-endpoint",
        label: "New Rest API Endpoint",
        icon: <KuikaSvg width={16} height={16} name="plus" />,
        hidden: hiddenUnlessOffered("new-rest-endpoint"),
        onSelect: (row: DatasourceListRow): void => {
          if (row.kind === "group") startNewRestEndpoint(this.getTreeActionDeps(), row.datasource);
        }
      },
      {
        key: "new-table",
        label: "New Table",
        icon: <KuikaSvg width={16} height={16} name="plus" />,
        hidden: hiddenUnlessOffered("new-table"),
        onSelect: (row: DatasourceListRow): void => {
          if (row.kind === "group") startNewTable(this.getTreeActionDeps(), row.datasource);
        }
      },
      {
        key: "new-folder",
        label: "New Folder",
        icon: <KuikaSvg width={16} height={16} name="folder-plus" type="sidebar-icons" />,
        hidden: hiddenUnlessOffered("new-folder"),
        onSelect: (row: DatasourceListRow): void => this.setState({ folderDialog: this.getNewFolderDialog(row) })
      },
      {
        key: "rename",
        label: "Rename",
        icon: <KuikaSvg width={16} height={16} name="edit" />,
        hidden: hiddenUnlessOffered("rename"),
        onSelect: this.openRenameDialog
      },
      {
        key: "unfile",
        label: "Move out of folder",
        icon: <KuikaSvg width={16} height={16} name="folder-off" type="sidebar-icons" />,
        // Dragging reaches only a group that is on screen; this is the way out from anywhere.
        hidden: hiddenUnlessOffered("unfile"),
        onSelect: (row: DatasourceListRow): void => this.moveRow(row, null)
      },
      {
        key: "delete",
        label: "Delete",
        icon: <KuikaSvg width={16} height={16} name="trash" />,
        danger: true,
        hidden: hiddenUnlessOffered("delete"),
        onSelect: (row: DatasourceListRow): void => this.setState({ rowToDelete: row })
      }
    ];
  };

  /** A Swagger-less REST action is deleted as the endpoint it is, as the tree names it. */
  getDeleteType = (): string => {
    const row: DatasourceListRow | undefined = this.state.rowToDelete;
    if (!row) return "item";
    if (getRowRestEndpointIndex(row) !== undefined) return "Rest API Endpoint";
    return row.kind;
  };

  getDeleteItemName = (): string => {
    const row: DatasourceListRow | undefined = this.state.rowToDelete;
    if (!row) return "";
    const endpointIndex: number | undefined = getRowRestEndpointIndex(row);
    if (row.kind === "action" && endpointIndex !== undefined) return getRestEndpointName(row.datasource, endpointIndex);
    return row.name;
  };

  /** Deleting an action an AI agent calls as a tool leaves that agent a broken reference: say so first. */
  getDeleteWarning = (): React.ReactNode => {
    const row: DatasourceListRow | undefined = this.state.rowToDelete;
    if (row?.kind !== "action") return undefined;
    const agents: string[] = findAgentUsagesOfAction(this.props.sd?.actionDefinitions ?? [], row.action.id);
    if (agents.length === 0) return undefined;
    const isOne: boolean = agents.length === 1;
    return (
      <span className="kuika-datasources__delete-warning">
        This action is used as a tool by {isOne ? "agent" : "agents"}: {agents.join(", ")}. Deleting it will leave a
        broken reference in {isOne ? "that agent" : "those agents"}.
      </span>
    );
  };

  deleteRow = async (): Promise<void> => {
    const row: DatasourceListRow | undefined = this.state.rowToDelete;
    if (!row) return;
    if (row.kind === "datasource") {
      await deleteDatasourceFromRow(this.props.sd, row.datasource);
      return;
    }
    if (row.kind === "table") {
      await deleteTableFromRow(this.props.sd, row.table);
      return;
    }
    if (row.kind === "action") {
      // A Swagger-less REST action is its endpoint: deleting only the action left the endpoint in
      // the datasource's connection.
      const endpointIndex: number | undefined = findRestEndpointIndex(row.datasource, row.action);
      if (endpointIndex !== undefined) {
        await deleteRestEndpointFromRow(this.props.sd, row.datasource, row.action, endpointIndex);
        return;
      }
      await deleteActionFromRow(this.props.sd, row.action);
      return;
    }
    if (row.kind === "folder") {
      if (row.folderKind === "action") {
        deleteDatasourceFolder(this.props.sd, { folderKind: "action", folder: row.folder });
      } else {
        deleteDatasourceFolder(this.props.sd, { folderKind: "table", folder: row.folder });
      }
      this.bumpFolderNonce();
    }
  };

  // --- rename ---------------------------------------------------------------

  closeRenameDialog = (): void => {
    this.renameStatus_.reset();
    this.setState({ renameTarget: null });
  };

  /** The tree's rename rules (`datasource-rename.ts`); an unchanged name is always acceptable. */
  getRenameProblem = (name: string): string | undefined => {
    const target: DatasourceRenameTarget | null = this.state.renameTarget;
    if (!target || name === getRenameTargetName(target)) return undefined;
    if (target.kind === "datasource") {
      return getDatasourceRenameProblem(name, getDatasourceRenameNames(this.props.sd, target.datasource));
    }
    return getTableRenameProblem(name, getTableRenameNames(this.props.sd, target.table));
  };

  validateRenameName = async (_rule: unknown, value: string): Promise<void> => {
    const problem: string | undefined = this.getRenameProblem(value ?? "");
    if (problem) throw new Error(problem);
  };

  /**
   * Stores the new name, holds the ✓, then closes and lights the row under its new name once the
   * refreshed list carries it. A failed write keeps the dialog open on "Try again"; the write has
   * already said why.
   */
  submitRename = (name: string): void => {
    const target: DatasourceRenameTarget | null = this.state.renameTarget;
    if (!target) return;
    // Renaming to the same name is not a write.
    if (name === getRenameTargetName(target)) {
      this.closeRenameDialog();
      return;
    }
    // The confirm is live again while its ✓ is up; a second click must not re-send the write.
    if (!this.renameStatus_.canStart) return;
    this.renameStatus_.start();
    const write: Promise<boolean> =
      target.kind === "datasource"
        ? renameDatasourceFromRow(this.props.sd, target.datasource, name)
        : renameTableFromRow(this.props.sd, target.table, name);
    void write.then((isStored: boolean): void => {
      if (!this.isMounted_) return;
      if (!isStored) {
        this.renameStatus_.fail();
        return;
      }
      this.renameStatus_.succeed((): void => this.setState({ renameTarget: null, revealRowName: name }));
    });
  };

  renderRenameDialog = (): React.ReactNode => {
    const target: DatasourceRenameTarget | null = this.state.renameTarget;
    const isTable: boolean = target?.kind === "table";
    return (
      <KuikaNameModal
        visible={target !== null}
        title={isTable ? "Rename table" : "Rename data source"}
        iconName={isTable ? "table" : "database"}
        iconType={isTable ? "common" : "datasource-icons"}
        confirmLabel="Rename"
        status={this.state.renameStatus}
        fieldName="datasourceRowName"
        placeholder={isTable ? "Enter table name" : "Enter data source name"}
        initialValue={target ? getRenameTargetName(target) : undefined}
        rules={[{ validator: this.validateRenameName }]}
        inputTestId="datasourceRowRenameInput"
        confirmTestId="datasourceRowRenameButton"
        wrapTestId="datasourceRowRenameModal"
        onCancel={this.closeRenameDialog}
        onSubmit={this.submitRename}
      />
    );
  };

  // --- folders --------------------------------------------------------------

  /** Repaints the tree now; see `folderNonce`. */
  bumpFolderNonce = (): void => {
    this.setState((prev: IDatasourcesLandingState): Pick<IDatasourcesLandingState, "folderNonce"> => ({
      folderNonce: prev.folderNonce + 1
    }));
  };

  /**
   * Moves an action or a table into a folder — or, with a null target, out of the one it is in.
   * The model decides whether the move means anything; this only carries it out.
   */
  moveRow = (row: DatasourceListRow, target: IDatasourceDropTarget | null): void => {
    const move: DatasourceRowMove | null = resolveDatasourceRowMove(row, target);
    if (!move) return;
    applyDatasourceRowMove(this.props.sd, move);
    this.bumpFolderNonce();
  };

  /** The folders and the role that decide what can be picked up; read once per render. */
  getRowDragContext = (): IDatasourceRowDragContext => ({
    actionFolders: this.props.sd?.project?.actionFolders ?? [],
    tableFolders: this.props.sd?.project?.databaseTablesFolders ?? [],
    canManageActions: canManageActions(this.props.sd),
    canManageTables: canManageTables(this.props.sd)
  });

  /**
   * Resolved against the tree as it stands when the drop lands: the row by its id, the target by
   * the drag group it was let go on. A cancelled drag moves nothing; one let go clear of every
   * group takes a filed row out of its folder, as on AI Functions.
   */
  handleRowDragEnd = (result: DropResult): void => {
    if (result.reason === "CANCEL") return;
    const rows: DatasourceListRow[] = flattenDatasourceRows(this.getRows());
    const dragged: DatasourceListRow | undefined = rows.find(
      (row: DatasourceListRow): boolean => row.id === result.draggableId
    );
    if (!dragged) return;
    const destinationId: string | undefined = result.destination?.droppableId;
    if (destinationId === result.source.droppableId) return;
    const target: IDatasourceDropTarget | null = destinationId
      ? (indexDatasourceDropTargets(rows).get(destinationId) ?? null)
      : null;
    // Let go on a group that takes nothing (a datasource row, Session Variables): no move.
    if (destinationId && !target) return;
    this.moveRow(dragged, target);
  };

  /**
   * Drag-and-drop is on only where something can move: a listed datasource has a folder of a kind
   * the user may manage. AI Functions' folders share `actionFolders` but hang off datasources this
   * page does not list, so they do not count. Without a target the handle column would be a grip
   * that goes nowhere.
   */
  hasDragTargets = (context: IDatasourceRowDragContext): boolean => {
    const listed: IDatasourceDto[] = (this.props.sd?.datasources ?? []).filter(isListableDatasource);
    const canMoveActions: boolean =
      context.canManageActions &&
      listed.some((datasource: IDatasourceDto): boolean => hasDatasourceFolderFor("action", datasource.id, context));
    const canMoveTables: boolean =
      context.canManageTables &&
      listed.some((datasource: IDatasourceDto): boolean => hasDatasourceFolderFor("table", datasource.id, context));
    return canMoveActions || canMoveTables;
  };

  closeFolderDialog = (): void => {
    this.setState({ folderDialog: null });
  };

  /** Resolves true so the dialog shows its ✓ and closes; the name rules already ran inside it. */
  submitFolderDialog = async (values: IFolderFormValues): Promise<boolean> => {
    const dialog: DatasourceFolderDialog | null = this.state.folderDialog;
    if (!dialog) return false;
    submitDatasourceFolderDialog(this.props.sd, dialog, values.name);
    this.bumpFolderNonce();
    return true;
  };

  folderNameExists = (name: string): boolean => {
    const dialog: DatasourceFolderDialog | null = this.state.folderDialog;
    if (!dialog) return false;
    return datasourceFolderNameExists(
      dialog,
      this.props.sd?.project?.actionFolders ?? [],
      this.props.sd?.project?.databaseTablesFolders ?? [],
      name
    );
  };

  renderFolderDialog = (): React.ReactNode => {
    const dialog: DatasourceFolderDialog | null = this.state.folderDialog;
    if (!dialog) return null;
    const isRename: boolean = dialog.mode === "rename";
    return (
      <FolderModal
        showModal={!isRename}
        closeModal={this.closeFolderDialog}
        showEditModal={isRename}
        closeEditModal={this.closeFolderDialog}
        editingFolderId={dialog.mode === "rename" ? dialog.folder.id : undefined}
        editingFolderName={dialog.mode === "rename" ? dialog.folder.name : undefined}
        onSubmit={this.submitFolderDialog}
        checkFolderNameExists={this.folderNameExists}
      />
    );
  };

  // --- table ----------------------------------------------------------------

  /** The disclosure every row that holds others leads with. */
  renderToggle = (row: DatasourceListRow): React.ReactNode => {
    const isOpen: boolean = this.isRowExpanded(row);
    return (
      <button
        type="button"
        className="kuika-datasources__toggle"
        aria-expanded={isOpen}
        aria-label={isOpen ? `Collapse ${row.name}` : `Expand ${row.name}`}
        onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
          e.stopPropagation();
          this.toggleRow(row);
        }}
      >
        <KuikaSvg type="arrows" name={isOpen ? "chevron-down" : "chevron-right"} width={14} height={14} />
      </button>
    );
  };

  /** "Actions" / "Tables" and a schema: the kind of thing below, and how many of them. */
  renderGroupNameCell = (row: DatasourceListRow, icon: React.ReactNode, secondary: string): React.ReactNode => (
    <span className="kuika-datasources__row">
      {this.renderToggle(row)}
      <span
        className="kuika-datasources__name-cell kuika-datasources__name-cell--group"
        data-testid={`${getRowTestIdName(row)}Row`}
      >
        <KuikaListTableNameCell name={row.name} icon={icon} secondary={secondary} />
      </span>
    </span>
  );

  /** A folder reads the way AI Functions' folders do: chevron, folder glyph, name, count. */
  renderFolderNameCell = (row: DatasourceListFolderRow): React.ReactNode => (
    <span className="kuika-datasources__row" data-testid={`${getRowTestIdName(row)}Row`}>
      {this.renderToggle(row)}
      <span className="kuika-datasources__folder-icon">
        <KuikaSvg name="folder" type="sidebar-icons" width={14} height={14} />
      </span>
      <KuikaTextMedium className="kuika-datasources__folder-name">{row.name}</KuikaTextMedium>
      <KuikaSmallText className="kuika-datasources__folder-count">
        {/* Rows drag between folders, so the count changes under the reader's eye. */}
        <KuikaNumber value={row.count} />
      </KuikaSmallText>
    </span>
  );

  renderNameCell = (row: DatasourceListRow): React.ReactNode => {
    if (row.kind === "group") {
      const isActions: boolean = row.groupKind === "actions";
      return this.renderGroupNameCell(
        row,
        <KuikaSvg type="common" name={isActions ? "bolt" : "table"} width={12} height={12} />,
        `${row.count} ${isActions ? "actions" : "tables"}`
      );
    }
    if (row.kind === "schema") {
      return this.renderGroupNameCell(
        row,
        <KuikaSvg type="common" name="layers" width={12} height={12} />,
        `${row.count} tables`
      );
    }
    if (row.kind === "folder") return this.renderFolderNameCell(row);
    if (row.kind === "sessionVariables") {
      return (
        <span className="kuika-datasources__name-cell" data-testid={`${getRowTestIdName(row)}Row`}>
          <KuikaListTableNameCell name={row.name} icon={<KuikaSvg type="common" name="key" width={12} height={12} />} />
        </span>
      );
    }
    if (row.kind === "datasource") {
      return (
        <span className="kuika-datasources__row">
          {this.renderToggle(row)}
          <span
            className="kuika-datasources__name-cell"
            data-testid={`${row.name}DatasourceRow`}
            style={
              {
                "--kuika-list-table-icon-bg": "var(--blue-1)",
                "--kuika-list-table-icon-color": "var(--accent-fg)"
              } as React.CSSProperties
            }
          >
            <KuikaListTableNameCell
              name={row.name}
              icon={
                <KuikaSvg type="datasource-icons" name={getDatasourceIconName(row.datasource)} width={12} height={12} />
              }
              secondary={`${row.tableCount} tables · ${row.actionCount} actions`}
            />
          </span>
        </span>
      );
    }

    const isTable: boolean = row.kind === "table";
    return (
      <span
        className="kuika-datasources__name-cell"
        data-testid={`${row.name}${isTable ? "Table" : "Action"}Row`}
        style={
          {
            "--kuika-list-table-icon-bg": isTable ? "var(--green-1)" : "var(--purple-1)",
            "--kuika-list-table-icon-color": isTable ? "var(--green-5)" : "var(--purple-5)"
          } as React.CSSProperties
        }
      >
        <KuikaListTableNameCell
          name={row.name}
          icon={<KuikaSvg type="common" name={isTable ? "table" : "bolt"} width={12} height={12} />}
        />
      </span>
    );
  };

  getColumns = (): KuikaListTableColumn<DatasourceListRow>[] => [
    // `KuikaTable` lays out fixed and shares the surplus in PROPORTION to the declared widths,
    // so an undeclared Name column falls back to tanstack's 150 and ends up the narrowest thing
    // on the row — with a data source's counts beside it, the name itself truncated to "Ma…".
    // The widths are also the table's floor before it scrolls sideways (`horizontalScroll`), so
    // together they stay under the column the landing gets beside the open Sources panel (~940px).
    { id: "name", header: () => "Name", width: 300, cell: this.renderNameCell },
    {
      id: "kind",
      header: () => "Kind",
      width: 120,
      // A group or a schema only arranges the rows below it, so it has no kind of its own to show.
      cell: (row: DatasourceListRow) => {
        if (row.kind === "datasource") return <KuikaListTableStatusCell label="Data source" color="blue" />;
        if (row.kind === "table") return <KuikaListTableStatusCell label="Table" color="green" />;
        if (row.kind === "action") return <KuikaListTableStatusCell label="Action" color="purple" />;
        if (row.kind === "folder") return <KuikaListTableStatusCell label="Folder" color="grey" />;
        if (row.kind === "sessionVariables") return <KuikaListTableStatusCell label="Variables" color="orange" />;
        return null;
      }
    },
    {
      id: "type",
      header: () => "Type",
      width: 160,
      cell: (row: DatasourceListRow) => {
        if (row.kind === "datasource" || row.kind === "action") return getDatasourceTypeLabel(row.datasource);
        if (row.kind === "table") return (row.table as IDatabaseTableModel).schema || "dbo";
        return null;
      }
    },
    {
      id: "created",
      header: () => "Created",
      width: 160,
      cell: (row: DatasourceListRow) => {
        // Rows that only arrange others carry nothing to date, not even a dash.
        if (row.kind !== "datasource" && row.kind !== "table" && row.kind !== "action") return null;
        // Only an action carries this. A datasource's row exists in the database with a
        // timestamp and an author, but the list DTO projects neither, and a table has none at all.
        if (row.kind !== "action") return <KuikaListTableUserCell fallback="—" />;
        const action: IActionDefinitionDto = row.action;
        return (
          <KuikaListTableUserCell
            caption={action.createdAt ? KMainFunctions.formatRelativeTime(action.createdAt) : undefined}
            name={
              action.createdUser
                ? `${action.createdUser.firstName ?? ""} ${action.createdUser.lastName ?? ""}`.trim()
                : undefined
            }
            photoUrl={action.createdUser?.profilePhotoUrl}
            // Without this the cell fell through to the "identity unknown" glyph for every
            // row, even the ones whose author it had just named. This payload carries only
            // the name, so the name is both the letters and the colour.
            initials={getCreatedUserInitials(action.createdUser)}
            fallback="—"
          />
        );
      }
    }
  ];

  renderTable = (): React.ReactNode => {
    const rows: IDatasourceListDatasourceRow[] = this.getRows();
    const isSearching: boolean = this.state.searchText.trim().length > 0 || this.state.kindFilter !== "all";
    const hasDatasources: boolean = (this.props.sd?.datasources ?? []).length > 0;
    // Every row in the tree, opened or not: which has a menu decides whether the list has an
    // actions column at all, so a role that can change nothing gets no column of empty cells.
    const allRows: DatasourceListRow[] = flattenDatasourceRows(rows);
    const menuOf: DatasourceRowMenuLookup = this.buildRowMenuLookup(allRows);
    const hasAnyMenu: boolean = allRows.some((row: DatasourceListRow): boolean => menuOf(row).length > 0);
    const dragContext: IDatasourceRowDragContext = this.getRowDragContext();

    return (
      <KuikaListTable<DatasourceListRow>
        data={rows}
        columns={this.getColumns()}
        rowKey={(row: DatasourceListRow) => row.id}
        rowActions={hasAnyMenu ? this.getRowActions(menuOf) : undefined}
        rowActionsTestId={(row: DatasourceListRow) => `${getRowTestIdName(row)}RowMenu`}
        onRowClick={(row: DatasourceListRow): void => this.handleRowClick(row, menuOf)}
        tree={{
          getSubRows: (row: DatasourceListRow): DatasourceListRow[] | undefined => getDatasourceListChildren(row),
          isRowExpanded: this.isRowExpanded,
          treeColumnId: "name"
        }}
        // Datasources and their contents are different kinds at different depths; a header sort
        // would scatter children away from the row they belong to.
        disableSorting
        rowClassName={(row: DatasourceListRow): string | undefined => {
          if (row.kind === "datasource") return "kuika-datasources__datasource-row";
          if (row.kind === "folder") return "kuika-datasources__folder-row";
          return this.isInertRow(row, menuOf) ? INERT_ROW_CLASS : undefined;
        }}
        // Actions and tables drag between their datasource's folders; every other row is a place
        // to land, not cargo. The model owns the groups and which drops mean something.
        dragDrop={
          this.hasDragTargets(dragContext)
            ? {
                getGroupId: getDatasourceRowDragGroupId,
                getDraggableId: (row: DatasourceListRow): string => row.id,
                isRowDraggable: (row: DatasourceListRow): boolean => isDatasourceRowDraggable(row, dragContext),
                handleLabel: "Drag to move into a folder",
                onDragEnd: this.handleRowDragEnd
              }
            : undefined
        }
        emptyIcon={
          <KuikaSvg
            name={isSearching ? "search-off" : "datasources"}
            type={isSearching ? "common" : "topbar-icons"}
            width={32}
            height={32}
          />
        }
        emptyVariant="page"
        emptyTitle={isSearching ? "No data sources found" : "No data sources yet"}
        emptyDescription={
          isSearching
            ? "We couldn't find any items matching your search."
            : hasDatasources
              ? "Every data source in this project is hidden by the current filter."
              : "Connect a database, an API or a runtime to start querying data."
        }
        // The row a save landed on lights up and fades back, so the reader's eye finds it in a
        // list that grew while they were inside an editor.
        revealRowKey={this.findRevealRowId(rows)}
        onRowRevealed={this.handleRowRevealed}
        data-testid="datasourcesTable"
      />
    );
  };

  /**
   * The path to the entity a closing editor asked for — a datasource, a table or an action; never
   * a group, schema or folder that happens to share its name.
   */
  findRevealPath = (rows: DatasourceListRow[]): DatasourceListRow[] | null => {
    const name: string | null = this.state.revealRowName;
    if (name === null) return null;
    return findDatasourceRowPath(
      rows,
      (row: DatasourceListRow): boolean =>
        (row.kind === "datasource" || row.kind === "table" || row.kind === "action") && row.name === name
    );
  };

  /**
   * The id of the row a closing editor asked for, among the rows currently loaded and drawn.
   * Returns null until the refreshed list carries it AND every row above it is open, which is what
   * keeps the reveal armed across the refresh and across `openPathToRevealRow`.
   */
  findRevealRowId = (rows: DatasourceListRow[]): string | null => {
    const path: DatasourceListRow[] | null = this.findRevealPath(rows);
    if (!path) return null;
    const ancestors: DatasourceListRow[] = path.slice(0, -1);
    if (!ancestors.every((row: DatasourceListRow): boolean => this.isRowExpanded(row))) return null;
    return path[path.length - 1].id;
  };

  /**
   * Opens every closed row above the one a closing editor asked for. The datasource arrives open
   * with the request (`parentId`); a group or folder the user closed earlier would still hide an
   * action saved inside it, and the reveal would never find a row to light.
   */
  openPathToRevealRow = (): void => {
    if (this.state.revealRowName === null) return;
    const path: DatasourceListRow[] | null = this.findRevealPath(this.getRows());
    if (!path) return;
    const closed: DatasourceListRow[] = path
      .slice(0, -1)
      .filter((row: DatasourceListRow): boolean => !this.isRowExpanded(row));
    if (closed.length === 0) return;
    const closedIds: string[] = closed.map((row: DatasourceListRow): string => row.id);
    this.setState((prev: IDatasourcesLandingState): Pick<IDatasourcesLandingState, "expandedIds" | "collapsedIds"> => ({
      expandedIds: [
        ...prev.expandedIds,
        ...closed
          .filter((row: DatasourceListRow): boolean => row.kind === "datasource" && !prev.expandedIds.includes(row.id))
          .map((row: DatasourceListRow): string => row.id)
      ],
      collapsedIds: prev.collapsedIds.filter((id: string): boolean => !closedIds.includes(id))
    }));
  };

  handleRowRevealed = (): void => {
    this.setState({ revealRowName: null });
  };

  /** Same tile vocabulary as the list's name cell, without the expand toggle: nothing nests here. */
  renderRecentNameCell = (row: DatasourceListRow): React.ReactNode => {
    const isTable: boolean = row.kind === "table";
    const isAction: boolean = row.kind === "action";
    let iconBg: string = "var(--blue-1)";
    let iconColor: string = "var(--blue-5)";
    if (isTable) {
      iconBg = "var(--green-1)";
      iconColor = "var(--green-5)";
    } else if (isAction) {
      iconBg = "var(--purple-1)";
      iconColor = "var(--purple-5)";
    }

    let icon: React.ReactNode = (
      <KuikaSvg type="datasource-icons" name={getDatasourceIconName(row.datasource)} width={12} height={12} />
    );
    if (isTable || isAction) {
      icon = <KuikaSvg type="common" name={isTable ? "table" : "bolt"} width={12} height={12} />;
    }

    return (
      <span
        className="kuika-datasources__name-cell"
        data-testid={`${row.name}RecentRow`}
        style={
          {
            "--kuika-list-table-icon-bg": iconBg,
            "--kuika-list-table-icon-color": iconColor
          } as React.CSSProperties
        }
      >
        <KuikaListTableNameCell name={row.name} icon={icon} />
      </span>
    );
  };

  /** Where the entity lives — the datasource, and for a table its schema too. */
  getRecentWhere = (row: DatasourceListRow): string => {
    if (row.kind === "datasource") return getDatasourceTypeLabel(row.datasource);
    // The display name, not `datasource.name`: the managed database's stored name is the
    // generated `<workspace>Datasource`, which the rest of the list never shows.
    const datasourceName: string = getDatasourceDisplayName(row.datasource);
    if (row.kind !== "table") return datasourceName;
    const schema: string = (row.table as IDatabaseTableModel).schema || "dbo";
    return `${datasourceName} · ${schema}`;
  };

  /**
   * Every sortable column carries the value it draws. The table offers a header sort on each of
   * them, and a column that renders through `cell` alone hands it nothing to compare — so the sort
   * ran and left the order exactly as it was. `modifiedAt` is the one that matters: it sorts on the
   * epoch behind "2 hours ago", which no ordering of that text could reproduce.
   */
  getRecentColumns = (): KuikaListTableColumn<IDatasourceRecentRow>[] => [
    {
      id: "name",
      header: () => "Name",
      // Like the Data sources tab, the widths are the floor before the table scrolls sideways and
      // stay under the column the landing gets beside the open Sources panel (~940px); a wider
      // column shares the surplus out in proportion.
      width: 260,
      // Lower-cased so the sort reads by name rather than by capitalisation, which would
      // otherwise put every lower-case name after every upper-case one.
      accessorFn: (entry: IDatasourceRecentRow) => entry.row.name.toLowerCase(),
      cell: (entry: IDatasourceRecentRow) => this.renderRecentNameCell(entry.row)
    },
    {
      id: "kind",
      header: () => "Kind",
      width: 120,
      accessorFn: (entry: IDatasourceRecentRow) => RECENT_KIND_LABELS[entry.row.kind],
      cell: (entry: IDatasourceRecentRow) => {
        if (entry.row.kind === "datasource") return <KuikaListTableStatusCell label="Data source" color="blue" />;
        if (entry.row.kind === "table") return <KuikaListTableStatusCell label="Table" color="green" />;
        return <KuikaListTableStatusCell label="Action" color="purple" />;
      }
    },
    {
      id: "where",
      header: () => "Where",
      width: 170,
      accessorFn: (entry: IDatasourceRecentRow) => this.getRecentWhere(entry.row).toLowerCase(),
      cell: (entry: IDatasourceRecentRow) => (
        <KuikaSmallText className="kuika-datasources__count">{this.getRecentWhere(entry.row)}</KuikaSmallText>
      )
    },
    // The same column every other landing draws, so an unattributed write and a photoless writer
    // read the same on every tool. The feed that dated this row inside the window dates its cell.
    buildLastModifiedColumn<IDatasourceRecentRow>(this.state.feed.index, (entry: IDatasourceRecentRow) =>
      getModifiedEntityId(entry.row)
    )
  ];

  getRecentEmptyTitle = (isFiltering: boolean): string => {
    if (isFiltering) return "Nothing here matches";
    return "Nothing changed recently";
  };

  getRecentEmptyDescription = (isFiltering: boolean): string => {
    if (isFiltering) return "We couldn't find any items matching your search.";
    // A failed feed is said out loud rather than shown as "nothing happened", which would be a
    // claim the page cannot actually make.
    if (this.state.feed.status === "failed") {
      return "We couldn't load recent activity — open Data sources to see everything.";
    }
    return `Data sources and actions edited in the last ${RECENTLY_MODIFIED_WINDOW_DAYS} days appear here.`;
  };

  /**
   * The feed's rows are resolved against the live model and the same row menu the list offers is
   * reused — a shortcut back in, not a second kind of list.
   */
  renderRecents = (feedRecents: IDatasourceRecent[]): React.ReactNode => {
    const entries: IDatasourceRecentRow[] = buildDatasourceRecentRows({
      recents: feedRecents,
      datasources: this.props.sd?.datasources ?? [],
      schemas: (this.props.sd?.schemas ?? new Map()) as Map<string, IDatasourceSchemaLike>,
      actions: this.props.sd?.actionDefinitions ?? [],
      searchText: this.state.searchText,
      kindFilter: this.state.kindFilter
    });
    const rows: DatasourceListRow[] = entries.map((entry: IDatasourceRecentRow): DatasourceListRow => entry.row);
    const menuOf: DatasourceRowMenuLookup = this.buildRowMenuLookup(rows);
    const hasAnyMenu: boolean = rows.some((row: DatasourceListRow): boolean => menuOf(row).length > 0);
    const isFiltering: boolean = this.state.searchText.trim().length > 0 || this.state.kindFilter !== "all";
    // This pane is only ever the recent tab's, and the page parks on that tab while the feed is in
    // flight. Without this it would paint an empty table first and claim nothing had changed
    // recently — something it cannot yet know.
    const isFeedPending: boolean = this.state.feed.status === "loading";
    // The page opens on this tab, so an editor's save-and-close usually lands here, with the saved
    // datasource or action as a fresh row. A saved table is never here; its reveal stays armed
    // until the full list is shown.
    const revealRowKey: string | null = this.findRevealRowId(rows);

    return (
      <KuikaListTable<IDatasourceRecentRow>
        data={entries}
        loading={isFeedPending && entries.length === 0}
        columns={this.getRecentColumns()}
        rowKey={(entry: IDatasourceRecentRow) => entry.row.id}
        rowActions={
          hasAnyMenu
            ? this.getRowActions(menuOf).map((action: KuikaListTableRowAction<DatasourceListRow>) => ({
                ...action,
                hidden: (entry: IDatasourceRecentRow) => action.hidden?.(entry.row) ?? false,
                onSelect: (entry: IDatasourceRecentRow) => action.onSelect(entry.row)
              }))
            : undefined
        }
        rowActionsTestId={(entry: IDatasourceRecentRow) => `${getRowTestIdName(entry.row)}RecentRowMenu`}
        // Nothing nests on this tab, so a row opens its editor or does nothing. The managed
        // database has no editor: on the Sources tree its row only opens its children, and here
        // there are none — its menu still leads to the ER diagram.
        onRowClick={(entry: IDatasourceRecentRow): void => {
          if (!isOpenableRow(entry.row) || !menuOf(entry.row).includes("edit")) return;
          this.openRow(entry.row);
        }}
        rowClassName={(entry: IDatasourceRecentRow): string | undefined =>
          menuOf(entry.row).includes("edit") ? undefined : INERT_ROW_CLASS
        }
        emptyIcon={<KuikaSvg name={isFiltering ? "search-off" : "history"} type="common" width={32} height={32} />}
        emptyVariant="page"
        emptyTitle={this.getRecentEmptyTitle(isFiltering)}
        emptyDescription={this.getRecentEmptyDescription(isFiltering)}
        revealRowKey={revealRowKey}
        onRowRevealed={this.handleRowRevealed}
        data-testid="datasourcesRecentsTable"
      />
    );
  };

  /** The diagram no longer has a tab to return to, so it says where it is and how to leave. */
  renderDiagramBack = (): React.ReactNode => (
    <div className="kuika-datasources__diagram-back">
      <KuikaButton
        type="tertiary"
        size="md"
        onClick={(): void => this.props.sd.setDatasourcesCentreView("list")}
        data-testid="datasourcesDiagramBackButton"
      >
        <KuikaSvg type="arrows" name="arrow-left" width={16} height={16} />
        Data sources
      </KuikaButton>
      <KuikaSmallText className="kuika-datasources__count">Managed DB · ER diagram</KuikaSmallText>
    </div>
  );

  renderDiagram = (): React.ReactNode => {
    // The diagram needs `databaseDiagramData`; the list does not, which is why this wait lives
    // here rather than in front of the whole page.
    if (this.props.sd?.databaseDiagramData?.error && this.props.sd.databaseDiagramData.error.length > 0) {
      return (
        <div className="kuika-datasources__diagram-error">
          <KuikaSmallText>{this.props.sd.databaseDiagramData.error}</KuikaSmallText>
        </div>
      );
    }
    if (this.props.sd?.isDatabaseDiagramDataLoading || !this.props.sd?.databaseDiagramData?.leftMenuData) {
      return (
        // The diagram's shape is not known until it is laid out, so it is the logo rather than a
        // skeleton — and in the diagram's own region: the tabs, the back control and the tree
        // beside it stay usable while it loads.
        <div className="kuika-datasources__diagram-spinner">
          <KuikaLoader variant="logo" size="lg" label="Loading the diagram" />
        </div>
      );
    }
    return (
      <div className="kuika-datasources__diagram">
        <DatabaseDiagram />
      </div>
    );
  };

  // --- create ---------------------------------------------------------------

  /** `ConnectorPickerModal`'s `onClose` — for its remaining, intent-driven openers. */
  closePicker = (): void => {
    this.setState({ pickerCategory: undefined });
  };

  /**
   * Opening a card's popover takes the slot; closing one only empties it if it still holds that
   * card. Clicking a second card closes the first (its outside-click) and opens the second in one
   * gesture — whichever of the two calls lands last, the second card stays open.
   */
  handleCardOpenChange = (category: DatasourceCreateCard, open: boolean): void => {
    this.setState(
      (prev: Readonly<IDatasourcesLandingState>): Pick<IDatasourcesLandingState, "openCardCategory"> | null => {
        if (open) return { openCardCategory: category };
        if (prev.openCardCategory !== category) return null;
        return { openCardCategory: undefined };
      }
    );
  };

  renderCreateCards = (): React.ReactNode => {
    if (!canManageDatasources(this.props.sd)) return null;

    const openCardCategory: DatasourceCreateCard | undefined = this.state.openCardCategory;
    // No onClick on the cards: each is its popover's trigger, and the trigger does the toggling.
    return (
      <KuikaCreateCardRow>
        <ConnectorPickerPopover
          category="database"
          label="Create a Database"
          sd={this.props.sd}
          open={openCardCategory === "database"}
          onOpenChange={(open: boolean): void => this.handleCardOpenChange("database", open)}
        >
          <KuikaCreateCard
            title="Create a Database"
            description="Connect MS SQL Server, PostgreSQL or Oracle."
            icon={<KuikaSvg type="datasource-icons" name="database" width={24} height={24} />}
            iconBackground={DATASOURCE_CARD_TINTS.database.background}
            iconColor={DATASOURCE_CARD_TINTS.database.color}
            open={openCardCategory === "database"}
            data-testid="datasourcesCreateDatabase"
          />
        </ConnectorPickerPopover>
        <ConnectorPickerPopover
          category="api"
          label="Create an API"
          sd={this.props.sd}
          open={openCardCategory === "api"}
          onOpenChange={(open: boolean): void => this.handleCardOpenChange("api", open)}
        >
          <KuikaCreateCard
            title="Create an API"
            description="Swagger, REST, OData, SAP — or an MQTT broker."
            icon={<KuikaSvg type="datasource-icons" name="cloud-cog" width={24} height={24} />}
            iconBackground={DATASOURCE_CARD_TINTS.api.background}
            iconColor={DATASOURCE_CARD_TINTS.api.color}
            open={openCardCategory === "api"}
            data-testid="datasourcesCreateApi"
          />
        </ConnectorPickerPopover>
        <ConnectorPickerPopover
          category="function"
          label="Create a Function"
          sd={this.props.sd}
          open={openCardCategory === "function"}
          onOpenChange={(open: boolean): void => this.handleCardOpenChange("function", open)}
        >
          <KuikaCreateCard
            title="Create a Function"
            description="C#, TypeScript, Python, Java, Go or Rust."
            icon={<KuikaSvg type="datasource-icons" name="hexagon" width={24} height={24} />}
            iconBackground={DATASOURCE_CARD_TINTS.function.background}
            iconColor={DATASOURCE_CARD_TINTS.function.color}
            open={openCardCategory === "function"}
            data-testid="datasourcesCreateFunction"
          />
        </ConnectorPickerPopover>
      </KuikaCreateCardRow>
    );
  };

  renderCentre = (
    view: DatasourcesCentreView,
    activeTab: string,
    feedRecents: IDatasourceRecent[]
  ): React.ReactNode => {
    if (view === "diagram") return this.renderDiagram();
    // The two tabs are two views of the same things: switching slides the new one in from the
    // side the selection moved towards instead of cutting.
    return (
      <KuikaTabContent
        className="kuika-datasources__tab-content"
        activeKey={activeTab}
        order={TAB_ORDER}
        render={(tab: string): React.ReactNode => this.renderTabPane(tab, feedRecents)}
      />
    );
  };

  renderTabPane = (tab: string, feedRecents: IDatasourceRecent[]): React.ReactNode => {
    if (tab === RECENTLY_MODIFIED_TAB) return this.renderRecents(feedRecents);
    return this.renderTable();
  };

  render() {
    // The provider decides landing-or-diagram only; which tab the landing shows is decided here.
    const view: DatasourcesCentreView = this.props.sd?.datasourcesCentreView ?? "list";
    const isDiagram: boolean = view === "diagram";
    // Built once per render: the tab choice and the recent pane both read it, and it walks every
    // action in the project.
    const feedRecents: IDatasourceRecent[] = this.getFeedRecents();
    const activeTab: string = this.getActiveTab(feedRecents);

    return (
      <>
        <KuikaListPage
          className={`kuika-datasources${isDiagram ? " kuika-datasources--diagram" : ""}`}
          data-testid="datasourcesScreen"
        >
          <KuikaListPageHeader
            icon={<KuikaSvg name="datasources" type="topbar-icons" width={20} height={20} />}
            title="Datasources"
            titleTestId="datasourcesTitle"
          />

          {!isDiagram && this.renderCreateCards()}

          <KuikaListPageToolbar
            tabs={
              isDiagram ? (
                this.renderDiagramBack()
              ) : (
                <KuikaTabs
                  size="lg"
                  options={[
                    // `label` is not optional in practice: KuikaTabs renders `icon || label`, so an
                    // option carrying only a value draws an empty pill.
                    {
                      value: RECENTLY_MODIFIED_TAB,
                      label: RECENTLY_MODIFIED_TAB,
                      dataTestId: RECENTLY_MODIFIED_TAB_TEST_ID
                    },
                    { value: LIST_TAB, label: LIST_TAB, dataTestId: "datasourcesListTab" }
                  ]}
                  value={activeTab}
                  onChange={(value: string): void => this.setState({ pickedTab: value })}
                  dataTestId="datasourcesTabs"
                />
              )
            }
            search={
              !isDiagram ? (
                <KuikaInput
                  size="md"
                  placeholder="Search by name..."
                  value={this.state.searchText}
                  leftIcon={<KuikaSvg name="search" width={14} height={14} />}
                  onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
                    this.setState({ searchText: String(value ?? "") })
                  }
                  data-testid="datasourcesSearchInput"
                />
              ) : undefined
            }
            filters={
              !isDiagram ? (
                <KuikaSelectbox
                  className="kuika-datasources__kind-filter"
                  size="md"
                  value={this.state.kindFilter}
                  options={KIND_FILTER_OPTIONS}
                  onChange={(value: DatasourceRowKind | "all"): void => this.setState({ kindFilter: value ?? "all" })}
                  data-testid="datasourcesKindFilter"
                />
              ) : undefined
            }
          />

          {this.renderCentre(view, activeTab, feedRecents)}
        </KuikaListPage>

        {/* The three cards open `ConnectorPickerPopover` above instead and never touch
            `pickerCategory` any more, and neither does the Sources tree's "+" (its own popover, on
            "all") — this instance now serves only the openers that ask for a category through the
            provider (`requestDatasourceCreate`): a switcher chip's "create new" ("all" or
            "function"). */}
        <ConnectorPickerModal
          visible={this.state.pickerCategory !== undefined}
          category={this.state.pickerCategory ?? "all"}
          onClose={this.closePicker}
        />
        <ActionUsagesModal
          showModal={this.state.actionToInspectUsages !== undefined}
          closeModal={(): void => this.setState({ actionToInspectUsages: undefined })}
          actionId={this.state.actionToInspectUsages?.id}
          actionName={this.state.actionToInspectUsages?.name}
        />
        <DeleteModal
          itemName={this.getDeleteItemName()}
          extraText={this.getDeleteWarning()}
          triggerModal={this.state.rowToDelete !== undefined}
          deleteItem={() => this.deleteRow()}
          deleteType={this.getDeleteType()}
          closeModal={(): void => this.setState({ rowToDelete: undefined })}
        />
        {this.renderFolderDialog()}
        {this.renderRenameDialog()}
      </>
    );
  }

  /**
   * The list is `sd.datasources` / `sd.schemas` / `sd.actionDefinitions`, all of which move
   * under change reasons this component cannot enumerate, so it follows the context rather
   * than filtering it.
   */
  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return true;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    setNextCallbackFunction: (cb: () => void) => dispatch(setNextCallbackFunction(cb)),
    showDomainAccessRequiredModal: (accessName: string | string[]) =>
      dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const datasourcesLanding = connect(null, mapDispatchToProps)(withSD<IDatasourcesLandingProps>(DatasourcesLanding));

export { datasourcesLanding as DatasourcesLanding };
